import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { Icon } from '@/design-system/components/icons/Icon';
import { getObjectScreenNameFromPath } from '@/design-system/components/navigatorEmbed/UtxAppNavigatorEmbed';
import { H6, TextCaption } from '@/design-system/components/typography';
import { AttachmentViewer } from '@/features/bill-payment/screens/components/attachmentViewer/AttachmentViewer';
import { DateFormat } from '@/utils';
import { DATE_FORMAT, formatDate } from '@/utils/dateFormatting';
import { iframePostMessage } from '@/utils/iframePostMessage';
import { StackActions, useFocusEffect, useNavigation } from '@react-navigation/native';
import { Pressable, ScrollView, SxProp, useDripsyTheme, View } from 'dripsy';
import _, { toNumber } from 'lodash';
import moment from 'moment-timezone';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutChangeEvent, Platform, useWindowDimensions } from 'react-native';
import { RootStackRoutes } from 'types';
import BillPaymentApp from '../..';
import SVGIconBillPaymentSuccess from '../../../../assets/svg/BillPaymentSuccess.svg';
import { IntervalBillPayment, ScheduleBillPayment } from '../../enum';
import { PAYMENT_TYPE, useCreateBillPayment } from '../../hooks/useCreateBillPayment.hook';
import { BillPaymentStackRoutes, BillPaymentSuccessProp } from '../../payment.navigator';
import { displayAmountWithCurrency, mapUrlLink, openImage64, openURL } from '../../utils/utils';
import { BillPaymentInfoItem } from '../components/BillPaymentInfoItem';

export const BillPaymentSuccessWebComponent: React.FC<BillPaymentSuccessProp> = ({
  response,
  submitResponse: { refId },
  debitAccount,
  documentSelected,
  paymentType,
  userFee
}) => {
  const navigation = useNavigation<any>();
  const { width } = useWindowDimensions();
  const { theme } = useDripsyTheme();
  const { $17, $30, $radius3 } = theme.space || ({} as any);
  const [sizeWidthButton, setSizeWithButton] = useState<number>(0);
  const widthButton = width > 1600 ? 269 : 240;
  const isColumnButton = sizeWidthButton < widthButton * 2;
  const { t } = useTranslation('global-bill-payment');

  const {
    general: { getThumbnailUrLFromResponse }
  } = useCreateBillPayment();

  const onLayout = (e: LayoutChangeEvent) => {
    const { width } = e.nativeEvent.layout;
    setSizeWithButton(width);
  };

  const renderScheduleDate = () => {
    if (_.isEmpty(response.attributes?.schedule)) {
      return <></>;
    }

    return (
      <>
        {response.attributes?.schedule &&
        response.attributes?.schedule.type?.toLowerCase() !== ScheduleBillPayment.RECURRING.toLowerCase() ? (
          <BillPaymentInfoItem
            title={t('global-bill-payment::glossary.scheduled-date')}
            description={
              !!response.attributes?.schedule?.endDate
                ? formatDate(response.attributes?.schedule?.endDate, { format: DATE_FORMAT.NO_TIME })
                : ' - '
            }
          />
        ) : (
          <BillPaymentInfoItem
            title={t('global-bill-payment::glossary.date-of-first-payment')}
            description={
              !!response.attributes?.schedule?.startDate
                ? formatDate(response.attributes?.schedule?.startDate, { format: DATE_FORMAT.NO_TIME })
                : ' - '
            }
          />
        )}
      </>
    );
  };

  const renderImage = () => {
    if (!_.isEmpty(getThumbnailUrLFromResponse(response))) {
      return (
        <Pressable onPress={async () => openURL(getThumbnailUrLFromResponse(response))}>
          <AttachmentViewer width={'100%'} padding={10} height={$30} imageUrl={getThumbnailUrLFromResponse(response)} />
        </Pressable>
      );
    }
    if (
      documentSelected &&
      documentSelected.type === 'success' &&
      (documentSelected.mimeType == 'image/png' || documentSelected.mimeType == 'image/jpeg')
    ) {
      return (
        <Pressable onPress={() => openImage64(documentSelected.uri)}>
          <AttachmentViewer width={'100%'} padding={10} height={$30} imageUrl={documentSelected.uri} />
        </Pressable>
      );
    }
    return <></>;
  };

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'web') {
        const onPopState = (event: any) => {
          window.history.pushState(event.state, '');
          setTimeout(() => {
            if (!document.location.href) return;
            const { parentName, subName } = mapUrlLink(document.location.href);
            var screenNameObject = getObjectScreenNameFromPath([BillPaymentApp], `/${subName}`);
            if (
              screenNameObject.screenName &&
              screenNameObject.routeStackName === parentName &&
              screenNameObject.screenName !== BillPaymentStackRoutes.Landing
            ) {
              navigation.dispatch(
                StackActions.replace(RootStackRoutes.BillPayment, {
                  screen: BillPaymentStackRoutes.Landing
                })
              );
            }
          }, 200);
        };
        window.addEventListener('popstate', onPopState);
        return () => {
          window.removeEventListener('popstate', onPopState);
        };
      }
    }, [])
  );

  return (
    <View sx={sxStyles.container}>
      <ScrollView showsVerticalScrollIndicator={false} sx={{ borderRadius: $radius3 }}>
        <View sx={sxStyles.body}>
          <View sx={sxStyles.viewRow}>
            <View sx={sxStyles.flex150}></View>
            <View sx={sxStyles.viewInfo}>
              <Icon name="success2" width={$17} height={$17} />
              <H6 bold sx={sxStyles.textGlobal}>
                {paymentType === PAYMENT_TYPE.GLOBAL
                  ? t('global-bill-payment::summary.title--overseas-instruction-placed')
                  : t('global-bill-payment::summary.title--local-bill-payment-placed')}
              </H6>
              <TextCaption sx={sxStyles.textNote}>{t('global-bill-payment::summary.text--allow-two-three-days')}</TextCaption>
              <View sx={sxStyles.fullWidth} onLayout={onLayout}>
                <View sx={sxStyles.viewAmountWrapper}>
                  <View sx={sxStyles.flex1}>
                    <TextCaption semiBold={true} sx={sxStyles.textGray}>
                      {t('global-bill-payment::glossary.merchant-bank-name')}
                    </TextCaption>
                    <H6 bold sx={sxStyles.textSuperMerchant}>
                      {response.attributes?.destination?.attributes?.recipient}
                    </H6>
                    <TextCaption semiBold sx={sxStyles.textBillAmount}>
                      {t('global-bill-payment::glossary.bill-amount')}
                    </TextCaption>
                    <H6 bold sx={sxStyles.textAmount}>
                      {displayAmountWithCurrency(response.attributes?.amount?.toString() ?? '', response.attributes?.asset ?? 'HKD')}
                    </H6>
                  </View>
                  <View sx={sxStyles.viewImageWrapper}>{renderImage()}</View>
                </View>
                <BillPaymentInfoItem
                  title={t('global-bill-payment::glossary.bill-bank-account-number')}
                  description={response.attributes?.destination?.attributes?.accountNumber ?? '-'}
                />
                <BillPaymentInfoItem
                  title={t('global-bill-payment::glossary.payment-additional-details')}
                  description={
                    _.isEmpty(response.attributes?.destination?.attributes?.paymentInfo)
                      ? '-'
                      : response.attributes?.destination?.attributes?.paymentInfo ?? '-'
                  }
                />
                <BillPaymentInfoItem
                  title={t('global-bill-payment::glossary.debit-account')}
                  description={_.isEmpty(debitAccount) ? '-' : debitAccount}
                />
                <BillPaymentInfoItem
                  title={t('global-bill-payment::glossary.instruction-fee')}
                  description={`${displayAmountWithCurrency(userFee, response.attributes?.asset ?? 'HKD')}`}
                  descriptionSx={{ color: '$danger' }}
                />
                <BillPaymentInfoItem
                  title={t('global-bill-payment::glossary.debit-amount')}
                  description={displayAmountWithCurrency(
                    `${Math.abs(toNumber(response.attributes?.amount) + parseFloat(userFee))}`,
                    response.attributes?.asset ?? 'HKD'
                  )}
                  descriptionSx={{ color: '#F5423E' }}
                />
                {renderScheduleDate()}
                {!_.isEmpty(response.attributes?.schedule?.interval) && (
                  <BillPaymentInfoItem
                    title={t('global-bill-payment::glossary.frequency')}
                    description={
                      response.attributes?.schedule?.interval === IntervalBillPayment.DAILY
                        ? t('global-bill-payment::glossary.daily')
                        : response.attributes?.schedule?.interval !== IntervalBillPayment.WEEKLY
                        ? t('global-bill-payment::glossary.monthly')
                        : t('global-bill-payment::glossary.weekly')
                    }
                  />
                )}
                {response.attributes?.schedule &&
                  response.attributes?.schedule.type?.toLowerCase() === ScheduleBillPayment.RECURRING.toLowerCase() && (
                    <BillPaymentInfoItem
                      title={t('global-bill-payment::glossary.payment-end-date')}
                      description={
                        !!response.attributes?.schedule?.endDate
                          ? formatDate(response.attributes?.schedule?.endDate, { format: DATE_FORMAT.NO_TIME })
                          : ' - '
                      }
                    />
                  )}
                <BillPaymentInfoItem
                  title={t('global-bill-payment::glossary.remarks')}
                  description={_.isEmpty(response.remarks) ? '-' : response.remarks ?? '-'}
                />
                <BillPaymentInfoItem
                  title={t('global-bill-payment::glossary.payment-instruction-time')}
                  description={moment().format(DateFormat.DATE_FORMAT.STANDARD)}
                />
                <BillPaymentInfoItem title={t('global-bill-payment::glossary.payment-ref-id')} description={refId} />
                <View sx={isColumnButton ? sxStyles.viewColumnButton : sxStyles.viewRowButton}>
                  <UniButton
                    variant={ButtonVariant.outline1}
                    sx={{ ...sxStyles.viewButton, ...{ width: widthButton } }}
                    labelSx={sxStyles.textReturn}
                    label={t('global-bill-payment::glossary.return')}
                    onPress={() => {
                      navigation.dispatch(
                        StackActions.replace(RootStackRoutes.BillPayment, {
                          screen: BillPaymentStackRoutes.Landing
                        })
                      );
                    }}
                  />
                  {isColumnButton && <View sx={sxStyles.viewDividerButton} />}
                  <UniButton
                    sx={{ ...sxStyles.viewButton, ...{ width: widthButton } }}
                    label={t('global-bill-payment::general.check-instruction-history')}
                    onPress={() => iframePostMessage('moveToBillPaymentList', true)}
                  />
                </View>
              </View>
            </View>
            <View sx={sxStyles.flex120}></View>
            <View sx={sxStyles.viewImage}>
              <SVGIconBillPaymentSuccess width={'100%'} />
            </View>
            <View sx={sxStyles.flex150}></View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    width: '100%',
    height: '100%'
  },
  body: {
    backgroundColor: '$backgroundPrimary',
    flex: 1,
    borderRadius: 15,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 10,
    overflow: 'hidden',
    mx: '$3',
    my: '$3'
  },
  fullWidth: {
    width: '100%'
  },
  flex1: {
    flex: 1
  },
  flex120: {
    flex: 120
  },
  flex150: {
    flex: 150
  },
  viewRow: {
    flexDirection: 'row',
    height: '100%'
  },
  viewInfo: {
    flex: 550,
    alignItems: 'center',
    height: '100%',
    py: '$11'
  },
  viewImage: {
    flex: 570,
    height: '100%',
    justifyContent: 'center'
  },
  textGlobal: {
    mt: '$1',
    mb: '$2',
    textAlign: 'center'
  },
  textNote: {
    mb: '$10',
    textAlign: 'center'
  },
  viewRowButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    mt: '$7'
  },
  viewColumnButton: {
    flexDirection: 'column',
    mt: '$7'
  },
  viewButton: {
    width: 269,
    height: '$11'
  },
  textReturn: {
    color: '$textPrimary'
  },
  textSuperMerchant: {
    color: '$secondary',
    mt: '$1',
    mb: '$5',
    lineHeight: 28
  },
  textAmount: {
    color: '$secondary'
  },
  textBillAmount: {
    mb: '$1',
    color: '#666666'
  },
  viewAmountWrapper: {
    width: '100%',
    flexDirection: 'row',
    minHeight: '$30',
    mb: '$5',
    justifyContent: 'space=between'
  },
  viewImageWrapper: {
    width: '$35',
    height: '$30'
  },
  viewDividerButton: {
    height: '$2'
  },
  textGray: {
    color: '#666666'
  }
};
